import AlertPopup from './ui/AlertPopup';
import common from './common';
import Logger from './utils/Logger';

export default class IsoTile extends PIXI.Container {
	constructor(tx, ty, world) {
		super();

		this._testWaterAnim = 0;

		this.quakeAmplitude = 2;
		this.quakeRate = 4;
		this.waveFrequency = 2.4;
		this.waveAmplitude = 4;

		this._world = world;
		this._neighbours = [];

		this._tx = tx;
		this._ty = ty;

		this._z = 0;
		this._ground = 0;
		this._structure = 0;
		this.previousStructure = -1;
		this._level = 0;
		this._risk = 0;
		this._strength = 0;
		this._damaged = false;
		this._riskFactor = 0;

		this.disabled = false;

		this._data = {
			ground: null,
			structure: null,
			upgrades: [],
		};

		this._defenceEffect = 0;
		this._quakeFrame = 0;

		this._isWater = false;
		this._isFloating = false;

		this._groundSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
		this.addChild(this._groundSprite);
		this._groundSprite.scale.set(0.5);

		this._riskSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
		this._riskSprite.scale.set(0.5);
		this._riskSprite.position.set(0, 7);
		this._riskSprite.visible = false;
		this.addChild(this._riskSprite);

		this._structureSprite = new PIXI.Sprite(PIXI.Texture.EMPTY);
		this._structureSprite.position.set(0, 54);
		this._structureSprite.scale.set(0.5);
		this._structureSprite.anchor.set(0, 1);

		this.addChild(this._structureSprite);

		this.setGroundTexture();
		this.setStructureTexture();
		this.setRiskTexture();

		this._alert = new AlertPopup();

		this._buildAnim = this._createBuildAnim();
		this._buildAnim.scale.set(0.5);
		this.addChild(this._buildAnim);

		this._destroyAnim = this._createDestroyAnim();
		this._destroyAnim.scale.set(0.5);
		this.addChild(this._destroyAnim);

		this._fireAnim = this._createFireAnim();
		this._fireAnim.scale.set(1);
		this.addChild(this._fireAnim);

		if (common.selectedLevel === 'wildfire') {
			this._mineAnim = this._createMineAnim();
			this._mineAnim.scale.set(0.5);
			this.addChild(this._mineAnim);
		}

		this._windAnim = null;
		this._waveAnim = null;
	}

	init(ground = 1, structure = 0, level = 0, pos = { x: 0, y: 0 }, disabled = false) {
		this._z = this.y;
		this.adjustZ(this._level);
		this.set(ground, structure, level, this.calculateRiskCategory(level), pos, disabled);

		this._alert.position.set(this.x + this.width * 0.5, this.y - 40);
		this._destroyAnim && this._destroyAnim.position.set(this.width * 0.5, 0);

		if (common.selectedLevel === 'hurricane' && this._data.structure && this._data.structure.group === 50) {
			this._windAnim = this._createPalmWindAnim();
			this._windAnim.scale.set(0.5);
			this.addChild(this._windAnim);
		}
	}

	destroy() {
		this._neighbours.length = 0;
		(this._alert && this._alert.parent) && this._alert.parent.removeChild(this._alert);

		this._fireAnim && this._fireAnim.destroy();
		this._mineAnim && this._mineAnim.destroy();
		this._waveAnim && this._waveAnim.destroy();
		this._windAnim && this._windAnim.destroy();
		this._buildAnim && this._buildAnim.destroy();
		this._destroyAnim && this._destroyAnim.destroy();

		super.destroy({ children: true });
	}

	calculateRiskCategory(level) {
		let riskCat = 0;

		if (common.selectedLevel !== 'wildfire') {
			if (level <= 6) {
				riskCat = 4;
				this._riskFactor = 0.8;
			} else if (level <= 12 && level > 6) {
				riskCat = 3;
				this._riskFactor = 1.2;
			} else if (level <= 18 && level > 12) {
				riskCat = 2;
				this._riskFactor = 1.4;
			} else if (level <= 24 && level > 18) {
				riskCat = 1;
				this._riskFactor = 2;
			} else if (level >= 30) {
				riskCat = 0;
				this._riskFactor = 2.5;
			}
		} else {
			for (const neighbour of this._neighbours) {
				if (!neighbour) {
					continue;
				}
				if (neighbour.data.structure && neighbour.data.structure.ignitable) {
					riskCat++;
				}
			}
			if (this.data.structure && !this.data.structure.ignitable) {
				riskCat = 0;
			}

			switch (riskCat) {
			case 0:
				this._riskFactor = 2.5;
				break;
			case 1:
				this._riskFactor = 2;
				break;
			case 2:
				this._riskFactor = 1.4;
				break;
			case 3:
				this._riskFactor = 1.2;
				break;
			case 4:
			default:
				this._riskFactor = 0.8;
				break;
			}
		}

		if (common.selectedLevel === 'hurricane') {
			if (this.data.structure) {
				riskCat--;
			}
			riskCat = P3.math.clamp(riskCat, 0, 4);
			switch (riskCat) {
			case 0:
				this._riskFactor = 2.5;
				break;
			case 1:
				this._riskFactor = 2;
				break;
			case 2:
				this._riskFactor = 1.4;
				break;
			case 3:
				this._riskFactor = 1.2;
				break;
			case 4:
			default:
				this._riskFactor = 0.8;
				break;
			}
		}

		this.risk = riskCat;

		return riskCat;
	}

	update(dt) {
		this.updateQuake();

		this.updateWater();

		if (this._waveAnim && this._waveAnim.visible) {
			this._waveAnim && this._waveAnim.update(dt);
		}

		if (this._alert && this._alert.visible) {
			this._alert && this._alert.update(dt);
		}

		if (this._destroyAnim && this._destroyAnim.visible) {
			this._destroyAnim && this._destroyAnim.update(dt);
		}

		if (this._fireAnim && this._fireAnim.visible) {
			this._fireAnim && this._fireAnim.update(dt);
		}

		if (this._buildAnim && this._buildAnim.visible) {
			this._buildAnim && this._buildAnim.update(dt);
		}

		if (this._mineAnim && this._mineAnim.visible) {
			this._mineAnim && this._mineAnim.update(dt);
		}

		if (this._windAnim && this._windAnim.visible) {
			this._windAnim && this._windAnim.update(dt);
		}
	}

	updateQuake() {
		if (this._world.isQuaking && !this._isWater) {
			const amplitude = this.quakeAmplitude * this._world.quakeScalar;

			++this._quakeFrame % this.quakeRate === 0 && (this.adjustZ(this._level + Math.random() * amplitude));
		}
	}

	updateWater() {
		if (this._isWater && !common.editor && common.motion) {
			const t = this.tx + this.ty + this._world.waveTime * this.waveFrequency;

			if (this._isFloating || this._structure === 0) {
				this.adjustZ(this._level - ((Math.sin(t) + 1) / 2) * this.waveAmplitude);
			}
		}
	}

	setNeighbours() {
		const stagger = this._ty % 2 === 0 ? -1 : 1;

		this._neighbours.length = 0;
		if (stagger === -1) {
			this._neighbours.push(this._world.getTile(this._tx + stagger, this._ty - 1)); // up
			this._neighbours.push(this._world.getTile(this._tx, this._ty + 1)); // down
			this._neighbours.push(this._world.getTile(this._tx + stagger, this._ty + 1)); // left
			this._neighbours.push(this._world.getTile(this._tx, this._ty - 1)); // right
		} else {
			this._neighbours.push(this._world.getTile(this._tx, this._ty - 1)); // up
			this._neighbours.push(this._world.getTile(this._tx + stagger, this._ty + 1)); // down
			this._neighbours.push(this._world.getTile(this._tx, this._ty + 1)); // left
			this._neighbours.push(this._world.getTile(this._tx + stagger, this._ty - 1)); // right
		}
	}

	setGroundTexture() {
		if (this.disabled) {
			this._groundSprite.texture = this._data.ground ? P3.assets.texture(`${this._data.ground.images.default}_x`) : PIXI.Texture.EMPTY;
		} else {
			this._groundSprite.texture = this._data.ground
				? P3.assets.texture(this._data.ground.images.default) : PIXI.Texture.EMPTY;
		}
	}

	setStructureTexture() {
		if (this._structure && this._structure !== -99) {
			if (this._data.structure) {
				if (this._data.structure.group === 207) {
					this.stopMineAnim();
				}

				let texture = PIXI.Texture.EMPTY;

				if (this._data.structure.images.damage && this._damaged) {
					texture = P3.assets.texture(this._data.structure.images.damage);
				} else if (this.disabled) {
					texture = P3.assets.texture(`${this._data.structure.images.default}_x`);
				} else {
					texture = P3.assets.texture(this._data.structure.images.default);
				}

				this._structureSprite.texture = texture;
			}
		} else {
			this._structureSprite.texture = PIXI.Texture.EMPTY;
		}
	}

	setRiskTexture() {
		let texture = P3.assets.texture('tile_risk_display');

		if (this.isWater) {
			texture = PIXI.Texture.EMPTY;
		}
		this._riskSprite.tint = common.riskColours[this._risk];
		this._riskSprite.texture = texture;
	}

	setStrength() {
		this._strength = 0;
		if (this._data.structure) {
			if (typeof this._data.structure.strength === 'object') {
				this._strength = P3.math.randomInt(this._data.structure.strength[0], this._data.structure.strength[1]);
			} else {
				this._strength = this._data.structure.strength;
			}

			this._strength = Math.floor(this._strength * this._riskFactor);
			for (const u of this.upgrades) {
				this._strength += this.world.getUpgrade(u).strength;
			}
			this._strength = Math.min(100, this._strength);
		}
	}

	adjustZ(z) {
		this.y = this._z - z;
	}

	set(ground, structure = 0, level = 0, risk = 0, pos, disabled) {
		this.ground = ground;
		this.structure = structure;
		this.level = level;
		this.risk = risk;
		this.disabled = disabled;
		this.arrayPos = pos;
	}

	playDestroyAnim() {
		if (!this._destroyAnim) {
			Logger.warn('No destroy animation found for this tile');

			return null;
		}

		this._destroyAnim.visible = true;
		this._destroyAnim.gotoAndPlay(0);

		return this._destroyAnim;
	}

	playBuildAnim() {
		if (!this._buildAnim) {
			Logger.warn('No build animation found for this tile');

			return null;
		}

		this._buildAnim.visible = true;
		this._buildAnim.gotoAndPlay(0);

		return this._buildAnim;
	}

	playFireAnim() {
		if (!this._fireAnim) {
			Logger.warn('No fire animation found for this tile');

			return null;
		}

		this._fireAnim.visible = true;
		this._fireAnim.position.set(this._world.tileWidth, -160);
		this._fireAnim.gotoAndPlay(0);

		return this._fireAnim;
	}

	playWindAnim() {
		if (!this._windAnim) {
			Logger.warn('No wind animation found for this tile');

			return null;
		}

		this._windAnim.visible = true;
		this._windAnim.position.set(this._world.tileWidth + 2, -55);
		this._windAnim.gotoAndPlay(0);
		this.previousStructure = this.structure;
		this.structure = -99;

		return this._windAnim;
	}

	playMineAnim() {
		if (!this._mineAnim) {
			Logger.warn('No mine animation found for this tile');

			return null;
		}

		this._mineAnim.visible = true;
		this._mineAnim.gotoAndPlay('0');
		this._structureSprite.visible = false;

		return this._mineAnim;
	}

	stopWindAnim() {
		if (!this._windAnim) {
			Logger.warn('No wind animation found for this tile');

			return null;
		}

		this._windAnim.stop();
		this._windAnim.visible = false;
		this.structure = this.previousStructure;
		this.setStructureTexture();

		return this._windAnim;
	}

	stopMineAnim() {
		if (this._mineAnim) {
			this._mineAnim.stop();
			this._mineAnim.visible = false;
		}
		this._structureSprite.visible = true;

		return this._mineAnim;
	}

	playWaveAnim() {
		if (!this._waveAnim) {
			return null;
		}

		this._waveAnim.visible = common.motion;
		this._waveAnim.position.set(this._world.tileWidth, -70);
		this._waveAnim.play();

		return this._waveAnim;
	}

	_createDestroyAnim() {
		const frames = [];

		for (let i = 0; i < 11; ++i) {
			frames.push(P3.assets.texture(`structure_destroyanim_${P3.utils.padNum(i, 2)}`));
		}

		const sequence = new P3.MovieClipSequence();

		sequence.addTextures(frames);

		const animation = new P3.MovieClip(sequence);

		animation.anchor.set(0.5);
		animation.animationSpeed = 24;
		animation.autoUpdate = false;
		animation.visible = false;
		animation.signals.animationComplete.add(() => {
			animation.visible = false;
		});

		return animation;
	}

	_createBuildAnim() {
		const name = 'structure_buildanim_$num$';

		/* if (common.selectedLevel !== 'tsunami') {
			name += '_x';
		} */

		const frames = [];

		for (let i = 0; i < 11; ++i) {
			frames.push(P3.assets.texture(name.replace('$num$', P3.utils.padNum(i, 2))));
		}

		const sequence = new P3.MovieClipSequence();

		sequence.addTextures(frames);

		const animation = new P3.MovieClip(sequence);

		animation.x += 40;
		animation.anchor.set(0.5);
		animation.animationSpeed = 24;
		animation.autoUpdate = false;
		animation.visible = false;
		animation.signals.animationComplete.add(() => {
			animation.visible = false;
		});

		return animation;
	}

	_createFireAnim() {
		const frames = [];
		const sequence = new P3.MovieClipSequence();

		for (let i = 0; i < 50; ++i) {
			frames.push(P3.assets.texture(`smokeanim_${P3.utils.padNum(i, 2)}`));	// fireanim was renamed to smokeanim
		}
		sequence.addTextures(frames.slice(), '0');

		const animation = new P3.MovieClip(sequence, '0');

		animation.position.set(this._world.tileWidth - 4, -60);
		animation.anchor.set(0.5, 0);
		animation.animationSpeed = 24;
		animation.looping = true;
		animation.autoUpdate = false;
		animation.visible = false;

		return animation;
	}

	_createPalmWindAnim() {
		const frames = [];
		const sequence = new P3.MovieClipSequence();

		for (let i = 0; i < 13; ++i) {
			frames.push(P3.assets.texture(`structure_hr_animated_palms_0_${P3.utils.padNum(i, 2)}`));
		}
		sequence.addTextures(frames.slice(), '0');

		const animation = new P3.MovieClip(sequence, '0');

		animation.position.set(this._world.tileWidth - 4, 0);
		animation.anchor.set(0.5, 0);
		animation.animationSpeed = 24;
		animation.looping = true;
		animation.autoUpdate = false;
		animation.visible = false;

		return animation;
	}

	_createMineAnim(dir) {
		const frames = [];
		const sequence = new P3.MovieClipSequence();

		if (dir === 'left') {
			for (let i = 0; i < 9; ++i) {
				frames.push(P3.assets.texture(`structure_wf_buildings_07_anim_${P3.utils.padNum(i, 2)}`));
			}
			sequence.addTextures(frames.slice(), '0');
		} else {
			for (let i = 0; i < 9; ++i) {
				frames.push(P3.assets.texture(`structure_wf_buildings_08_anim_${P3.utils.padNum(i, 2)}`));
			}
			sequence.addTextures(frames.slice(), '0');
		}

		const animation = new P3.MovieClip(sequence, '0');

		animation.position.set(this._world.tileWidth - 4, -50);
		animation.anchor.set(0.5, 0);
		animation.animationSpeed = 24;
		animation.looping = true;
		animation.autoUpdate = false;
		animation.visible = false;

		return animation;
	}

	_createWaveAnim() {
		const frames = [];

		for (let i = 0; i < 60; ++i) {
			frames.push(P3.assets.texture(`ground_waveanim1_${P3.utils.padNum(i, 2)}`));
		}

		const sequence = new P3.MovieClipSequence();

		sequence.addTextures(frames);

		const animation = new P3.MovieClip(sequence);

		animation.scale.set(0.6);
		animation.position.set(this._world.tileWidth - 4, 0);
		animation.anchor.set(0.5, 0);
		animation.animationSpeed = 24;
		animation.looping = true;
		animation.autoUpdate = true;
		animation.visible = true;

		return animation;
	}

	get world() {
		return this._world;
	}

	get tx() {
		return this._tx;
	}

	get ty() {
		return this._ty;
	}

	get z() {
		return this._z;
	}

	get data() {
		return this._data;
	}

	get ground() {
		return this._ground;
	}

	set ground(value) {
		this._ground = value;
		this._data.ground = this._world.getGround(value);

		if (this._waveAnim) {
			this._waveAnim.stop();
			this._waveAnim.destroy({ children: true });
			this._waveAnim = null;
		}

		if (value === 99) {
			this._waveAnim = this._createWaveAnim();
			this.addChild(this._waveAnim);
			this.playWaveAnim();

			common.motionToggle.add(() => {
				this._waveAnim.visible = common.motion;
			});
		}

		this.setGroundTexture();

		if (this._data.ground) {
			this._isWater = typeof this._data.ground.water === 'boolean' ? this._data.ground.water : false;
			this.waveAmplitude = this._data.ground.wave ? this._data.ground.wave.amplitude : this.waveAmplitude;
			this.waveFrequency = this._data.ground.wave ? this._data.ground.wave.frequency : this.waveFrequency;
		}

		this.adjustZ(this._level);
	}

	get structure() {
		return this._structure;
	}

	set structure(value) {
		this._structure = value;
		this._data.structure = this._world.getStructure(value);

		this.setStructureTexture();
		this.setStrength();

		this._data.structure && (this._isFloating = typeof this._data.structure.floating === 'boolean'
			? this._data.structure.floating : true);
	}

	get level() {
		return this._level;
	}

	set level(value) {
		this._level = value;
		this.adjustZ(this._level);
		// this.updateWater();
		this.setStrength();
	}

	get risk() {
		return this._risk;
	}

	set risk(value) {
		this._risk = value;
		this.setRiskTexture();
		this.setStrength();
	}

	get riskFactor() {
		return this._riskFactor;
	}

	set riskFactor(v) {
		this._riskFactor = v;
	}

	get strength() {
		return this._strength;
	}

	get damaged() {
		return this._damaged;
	}

	set damaged(value) {
		this._damaged = value;
		this.setStructureTexture();
	}

	get neighbours() {
		return this._neighbours;
	}

	get upgrades() {
		return this._data.upgrades;
	}

	set upgrades(value) {
		this._data.upgrades.push(value);
	}

	get up() {
		return this._neighbours[0];
	}

	get down() {
		return this._neighbours[1];
	}

	get left() {
		return this._neighbours[2];
	}

	get right() {
		return this._neighbours[3];
	}

	get groundSprite() {
		return this._groundSprite;
	}

	get structureSprite() {
		return this._structureSprite;
	}

	get riskSprite() {
		return this._riskSprite;
	}

	get isWater() {
		return this._isWater;
	}

	get isFloating() {
		return this._isFloating;
	}

	set isFloating(value) {
		this._isFloating = value;
	}

	get alert() {
		return this._alert;
	}

	get defenceEffect() {
		return this._defenceEffect;
	}

	set defenceEffect(v) {
		this._defenceEffect = v;
	}
}
